import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "../store";
import { confirmBaggageCart } from "../thunks";

// =============================
// Types & State
// -----------------------------
// Keep domain types and the Redux state shape here.
// Selectors can live in this file (co-located) or a separate selectors file when they grow.

export type PassengerEligibility = {
  [key: string]: string; // passengerKey: ssrCode
};

export type DirectionEligibility = {
  outbound: PassengerEligibility;
  inbound: PassengerEligibility;
};

export interface BaggageCart {
  journeyKey: string;
  passengers: {
    passengerTypeCode: string;
    passengerKey: string;
    bundleCode: string | null;
    bundleName: string | null;
    bags: Array<{ ssrCode: string; quantity: number; amount: number }>;
  }[];
  isWithBundle?: boolean;
}

interface BaggagePricing {
  passengerKey: string;
  ssrCode: string;
  price: number;
  name: string;
}

export interface BaggagePricePerTrip {
  outbound: BaggagePricing[];
  return: BaggagePricing[];
}

export interface PrevCheckInBaggage {
  outbound: {
    passenger?: string;
    baggages?: string[];
  }[];
  inbound: {
    passenger?: string;
    baggages?: string[];
  }[];
}

export interface BaggageState {
  journeyKey: string;
  optOutAdditionalBaggage: boolean;
  isBaggageStepValid: boolean;
  confirmed: boolean;
  isLoading: boolean;
  openAccordion: string[];
  baggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
  bookingBaggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
  checkInBaggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
  prevCheckInBaggage: PrevCheckInBaggage;
  baggagePricePerTrip: BaggagePricePerTrip;
  isCheckInClosedForOutboundFlight: boolean;
  isCheckInClosedForInboundFlight: boolean;
  baggageEligibleForOneDollar: DirectionEligibility;
}

// =============================
// Initial State
// -----------------------------
const initialState: BaggageState = {
  journeyKey: "",
  confirmed: false,
  optOutAdditionalBaggage: false,
  isBaggageStepValid: true,
  isLoading: false,
  openAccordion: [],
  baggageCart: {},
  bookingBaggageCart: {},
  checkInBaggageCart: {},
  prevCheckInBaggage: { outbound: [], inbound: [] },
  baggagePricePerTrip: { outbound: [], return: [] },
  isCheckInClosedForOutboundFlight: false,
  isCheckInClosedForInboundFlight: false,
  baggageEligibleForOneDollar: {
    outbound: {},
    inbound: {},
  },
};

// =============================
// Slice: reducers + generated action creators
// -----------------------------
const baggageSlice = createSlice({
  name: "baggage",
  initialState,
  reducers: {
    resetBaggageState: (state) => {
      return {
        ...initialState,
        prevCheckInBaggage: state.prevCheckInBaggage,
      };
    },
    setJourneyKey: (state, action: PayloadAction<string>) => {
      state.journeyKey = action.payload;
    },
    clearBaggage: (state) => {
      state.baggageCart = {};
      state.confirmed = false;
    },
    confirmBaggage: (state) => {
      state.confirmed = true;
    },
    unconfirmBaggage: (state) => {
      state.confirmed = false;
    },
    setOptOutAdditionalBaggage: (state, action: PayloadAction<boolean>) => {
      state.optOutAdditionalBaggage = action.payload;
    },
    setIsBaggageStepValid: (state, action: PayloadAction<boolean>) => {
      state.isBaggageStepValid = action.payload;
    },
    setBaggageLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setOpenAccordion: (state, action: PayloadAction<string[]>) => {
      state.openAccordion = action.payload;
    },
    setBaggageCart: (
      state,
      action: PayloadAction<{ outbound?: BaggageCart; inbound?: BaggageCart }>,
    ) => {
      state.baggageCart = action.payload;
    },
    setPrevCheckInBaggage: (state, action: PayloadAction<PrevCheckInBaggage>) => {
      state.prevCheckInBaggage = action.payload;
    },
    upsertPassengerBag: (
      state,
      action: PayloadAction<{
        direction: "outbound" | "inbound";
        passengerKey: string;
        bag: { ssrCode: string; quantity: number; amount: number };
      }>,
    ) => {
      const { direction, passengerKey, bag } = action.payload;
      const journey = state.baggageCart?.[direction];
      if (!journey) return;
      const pax = journey.passengers?.find((p) => p.passengerKey === passengerKey);
      if (!pax) return;

      // Remove if quantity is zero or negative
      if ((bag?.quantity ?? 0) <= 0) {
        pax.bags = (pax.bags ?? []).filter((b) => b.ssrCode !== bag.ssrCode);
        return;
      }

      const existing = (pax.bags ?? []).find((b) => b.ssrCode === bag.ssrCode);
      if (existing) {
        existing.quantity = bag.quantity;
        existing.amount = bag.amount;
      } else {
        pax.bags = [...(pax.bags ?? []), bag];
      }
    },
    setBaggagePricePerTrip: (state, action: PayloadAction<BaggagePricePerTrip>) => {
      state.baggagePricePerTrip = action.payload;
    },
    restoreBaggageCartFromConfirmed: (state) => {
      state.baggageCart = { ...state.bookingBaggageCart };
    },
    clearBaggageCart: (state) => {
      state.baggageCart = {};
      state.bookingBaggageCart = {};
      state.checkInBaggageCart = {};
    },
    setCheckInBaggageCart: (
      state,
      action: PayloadAction<{ outbound?: BaggageCart; inbound?: BaggageCart }>,
    ) => {
      state.checkInBaggageCart = action.payload;
    },
    setIsCheckInClosedForOutboundFlight: (state, action: PayloadAction<boolean>) => {
      state.isCheckInClosedForOutboundFlight = action.payload;
    },
    setIsCheckInClosedForInboundFlight: (state, action: PayloadAction<boolean>) => {
      state.isCheckInClosedForInboundFlight = action.payload;
    },
    setBaggageEligibleForOneDollar: (
      state,
      action: PayloadAction<Partial<DirectionEligibility>>,
    ) => {
      state.baggageEligibleForOneDollar = {
        ...state.baggageEligibleForOneDollar,
        ...action.payload,
      };
    },
    setPaxBaggageEligibleForOneDollar: (
      state,
      action: PayloadAction<{
        direction: "outbound" | "inbound";
        passengerKey: string;
        ssrCode: string;
      }>,
    ) => {
      state.baggageEligibleForOneDollar = {
        ...state.baggageEligibleForOneDollar,
        [action.payload.direction]: {
          ...state.baggageEligibleForOneDollar[action.payload.direction],
          [action.payload.passengerKey]: action.payload.ssrCode,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(confirmBaggageCart.pending, (state) => {
        state.bookingBaggageCart = { ...state.baggageCart };
        state.isLoading = true;
      })
      .addCase(confirmBaggageCart.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(confirmBaggageCart.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

// =============================
// Selectors
// -----------------------------
export const selectJourneyKey = (s: RootState) => s.baggage.journeyKey;

export const selectOptOutAdditionalBaggage = (s: RootState) =>
  Boolean(s.baggage?.optOutAdditionalBaggage);

export const selectHasAnyBaggageSelected = (s: RootState) => {
  const { outbound, inbound } = s.baggage?.bookingBaggageCart ?? {};
  const journeys = [outbound, inbound].filter(Boolean) as BaggageCart[];
  return journeys.some((journey) =>
    (journey.passengers ?? []).some((p) => (p.bags ?? []).some((bag) => (bag?.quantity ?? 0) > 0)),
  );
};

export const selectIsBaggageStepValid = (s: RootState) => s.baggage.isBaggageStepValid;

export const selectOpenAccordion = (s: RootState): string[] => s.baggage.openAccordion;

export const selectBaggageCart = (
  s: RootState,
): { outbound?: BaggageCart; inbound?: BaggageCart } | undefined => s.baggage.baggageCart;

export const selectOutboundBaggageCart = (s: RootState): BaggageCart | undefined =>
  s.baggage.baggageCart?.outbound;

export const selectInboundBaggageCart = (s: RootState): BaggageCart | undefined =>
  s.baggage.baggageCart?.inbound;

export const selectBaggageState = (s: RootState): BaggageState => s.baggage;

export const selectBaggageConfirmed = (s: RootState) => s.baggage.confirmed;

export const selectIsBaggageLoading = (s: RootState) => s.baggage.isLoading;

export const selectTotalAmount = (s: RootState) => {
  const { outbound, inbound } = s.baggage?.baggageCart ?? {};
  const journeys = [outbound, inbound].filter(Boolean) as BaggageCart[];
  return journeys.reduce((total, journey) => {
    return (
      total +
      (journey.passengers?.reduce((passengerTotal, passenger) => {
        return (
          passengerTotal +
          (passenger.bags?.reduce((bagTotal, bag) => {
            return bagTotal + bag.amount;
          }, 0) ?? 0)
        );
      }, 0) ?? 0)
    );
  }, 0);
};

export const selectBaggagePricePerTrip = (s: RootState) => s.baggage.baggagePricePerTrip;

export const selectBookingBaggageCart = (s: RootState) => s.baggage.bookingBaggageCart;

export const selectCheckInBaggageCart = (s: RootState) => s.baggage.checkInBaggageCart;

export const selectCheckInCart = (state: RootState) => state.baggage.checkInBaggageCart;

export const selectPrevCheckInBaggage = (state: RootState) => state.baggage.prevCheckInBaggage;

export const selectIsCheckInClosedForOutboundFlight = (s: RootState) =>
  s.baggage.isCheckInClosedForOutboundFlight;

export const selectIsCheckInClosedForInboundFlight = (s: RootState) =>
  s.baggage.isCheckInClosedForInboundFlight;

export const selectBaggageEligibleForOneDollar = (s: RootState) =>
  s.baggage.baggageEligibleForOneDollar;

// =============================
// Action Creators
// -----------------------------
export const {
  resetBaggageState,
  setJourneyKey,
  clearBaggage,
  confirmBaggage,
  unconfirmBaggage,
  setOptOutAdditionalBaggage,
  setIsBaggageStepValid,
  setBaggageLoading,
  setOpenAccordion,
  setBaggageCart,
  upsertPassengerBag,
  setBaggagePricePerTrip,
  restoreBaggageCartFromConfirmed,
  clearBaggageCart,
  setCheckInBaggageCart,
  setIsCheckInClosedForOutboundFlight,
  setIsCheckInClosedForInboundFlight,
  setBaggageEligibleForOneDollar,
  setPaxBaggageEligibleForOneDollar,
  setPrevCheckInBaggage,
} = baggageSlice.actions;

// =============================
// Reducer
// -----------------------------
export default baggageSlice.reducer;
